import { useCallback, useRef, useState } from 'react';
import { decoupledSave } from '../../services/api';
import { extractChanges, syncItemsMeta } from './menuTreeApi';

/**
 * Drag-and-drop logic for the menu admin tree.
 *
 * On drop we keep the new tree locally (no refetch): we diff the moved links
 * against their previous parent/weight, persist only those via the generic
 * decoupled save, and update the in-memory meta so the state stays consistent.
 * On error we roll back to the pre-drop tree.
 *
 * @param {Object} opts
 * @param {Array} opts.items - current dnd tree items.
 * @param {Function} opts.setItems - state setter for the tree items.
 * @param {string} opts.bundle - menu_link_content bundle.
 */
export default function useMenuTreeDnd({ items, setItems, bundle }) {
  const snapshotRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const handleItemsChanged = useCallback((newItems, reason) => {
    // Collapse/expand and in-between moves: reflect immediately.
    if (reason?.type !== 'dropped') {
      setItems(newItems);
      return;
    }

    const snapshot = items;
    const changes = extractChanges(newItems);       // diff using the OLD meta
    const synced = syncItemsMeta(newItems);          // refresh parentKey + weight
    setItems(synced);

    if (changes.length === 0) return;

    snapshotRef.current = snapshot;
    setSaving(true);
    Promise.all(
      changes.map((c) =>
        decoupledSave('menu_link_content', bundle, { parent: c.parent, weight: c.weight }, c.id)),
    )
      .catch((err) => {
        console.error('[MenuTree] Reorder failed:', err.message);
        if (snapshotRef.current) setItems(snapshotRef.current);
      })
      .finally(() => {
        snapshotRef.current = null;
        setSaving(false);
      });
  }, [items, setItems, bundle]);

  return { handleItemsChanged, saving };
}
